import React, { useEffect, useState } from 'react';
import type { Pathway, PathwayBlock } from '../types';
import { DataService } from '../services/firebase';
import { Modal } from './Common';
import { PathwayProgressFlow } from './PathwayProgressFlow';
import { ChevronLeft } from 'lucide-react';

interface PathwayProgressViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    candidateName: string;
    candidateId: string;
    pathwayId: string;
    selectedTrack?: string;
}

export const PathwayProgressViewerModal: React.FC<PathwayProgressViewerModalProps> = ({
    isOpen,
    onClose,
    candidateName,
    candidateId,
    pathwayId,
    selectedTrack,
}) => {
    const [pathway, setPathway] = useState<Pathway | null>(null);
    const [blockProgressMap, setBlockProgressMap] = useState<Map<string, string>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [activePathwayId, setActivePathwayId] = useState(pathwayId);
    const [pathwayStack, setPathwayStack] = useState<string[]>([]);

    // Reset when the modal opens with a new pathwayId
    useEffect(() => {
        setActivePathwayId(pathwayId);
        setPathwayStack([]);
    }, [pathwayId, isOpen]);

    useEffect(() => {
        if (!isOpen || !candidateId || !activePathwayId) return;
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setPathway(null);
            setBlockProgressMap(new Map());
            try {
                const [pw, progress] = await Promise.all([
                    DataService.getPathwayByRootId(activePathwayId),
                    DataService.getBlockProgressForPathway(candidateId, activePathwayId),
                ]);
                if (cancelled) return;
                setPathway(pw || null);
                const map = new Map<string, string>();
                (progress as any[]).forEach((p: any) => map.set(p.blockId, p.status));
                setBlockProgressMap(map);
            } catch (e) {
                console.error('Failed to load pathway progress viewer', e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen, candidateId, activePathwayId]);

    const handleSelectBlock = (block: PathwayBlock) => {
        if (block.type === 'pathway' && block.linkedPathwayId) {
            setPathwayStack(prev => [...prev, activePathwayId]);
            setActivePathwayId(block.linkedPathwayId);
        }
    };

    const handleBack = () => {
        const stack = [...pathwayStack];
        const previous = stack.pop();
        if (previous) {
            setPathwayStack(stack);
            setActivePathwayId(previous);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${candidateName} — Pathway Progress`}
            maxWidth="max-w-2xl"
        >
            {pathwayStack.length > 0 && (
                <button
                    onClick={handleBack}
                    className="flex items-center gap-1 text-sm text-aero-500 hover:text-aero-700 mb-2 transition-colors"
                >
                    <ChevronLeft className="w-4 h-4" /> Back to parent pathway
                </button>
            )}
            {isLoading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600" />
                </div>
            ) : !pathway ? (
                <div className="text-center py-12 text-aero-400 italic">
                    Could not load pathway data.
                </div>
            ) : (
                <div className="max-h-[70vh] overflow-y-auto">
                    <PathwayProgressFlow
                        pathway={pathway}
                        blockProgressMap={blockProgressMap}
                        selectedTrack={pathwayStack.length === 0 ? selectedTrack : undefined}
                        onSelectBlock={handleSelectBlock}
                    />
                </div>
            )}
        </Modal>
    );
};
